import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndContext, DragOverlay, rectIntersection, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, ChevronLeft, ChevronRight, Calendar, Layout, Clock, Users, FileText, Package, Truck, Hammer, DollarSign, Briefcase, GraduationCap, CreditCard, FileCheck, AlertCircle, Archive, GripVertical, Bell, Pin, Edit2, Trash2, CheckCircle, ArrowDownCircle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { dashboardService } from '../services/dashboardService';
import { userService } from '../services/api';
import annotationsService from '../services/annotationsService';

// API functions
const fetchTasks = async (filters = {}) => {
  const res = await dashboardService.getTasks(filters);
  return res.data;
};

const FINANCEIRO_COLUMNS = [
  { id: 'overdue', title: 'Atrasado', color: 'bg-red-50 border-red-300', headerColor: 'bg-red-100', icon: AlertCircle },
  { id: 'due_soon', title: 'A Vencer', color: 'bg-orange-50 border-orange-300', headerColor: 'bg-orange-100', icon: Bell },
  { id: 'em_tratativa', title: 'Em tratativa', color: 'bg-yellow-50 border-yellow-300', headerColor: 'bg-yellow-100', icon: Clock },
  { id: 'resolvido', title: 'Resolvido', color: 'bg-green-50 border-green-300', headerColor: 'bg-green-100', icon: Package }
];

const TAREFAS_COLUMNS = [
  { id: 'overdue', title: 'Atrasado', color: 'bg-red-50 border-red-300', headerColor: 'bg-red-100', icon: AlertCircle },
  { id: 'pending', title: 'A Fazer', color: 'bg-gray-100 border-gray-300', headerColor: 'bg-gray-200', icon: FileText },
  { id: 'annotations', title: 'Anotações', color: 'bg-purple-50 border-purple-300', headerColor: 'bg-purple-100', icon: FileText, isAnnotations: true, width: 'w-80' }
];

const COLUMN_WIDTH = 'w-56';

const COLORS = [
  { name: 'yellow', bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-700' },
  { name: 'green', bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700' },
  { name: 'blue', bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700' },
  { name: 'red', bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-700' },
  { name: 'purple', bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700' },
  { name: 'orange', bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700' }
];

const ITEMS_PER_PAGE = 5;

const formatCurrency = (val) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
};

// Source icons
const SOURCE_ICONS = {
  agendamentos: Calendar,
  projetos: FileText,
  visitas: Users,
  demandas: FileText,
  'pre-producao': Package,
  entregas: Truck,
  'contatos-producao': Hammer,
  montagem: Hammer,
  embalagem: Package,
  orcamentos: DollarSign,
  'horas-extras': Clock,
  'sabados-trabalhados': Calendar,
  ausencias: Users,
  ferias: Calendar,
  holerites: CreditCard,
  ponto: Clock,
  treinamentos: GraduationCap,
  despesas: DollarSign,
  adiantamentos: DollarSign,
  'documentos-fiscais': FileText,
  contratos: FileCheck,
  'horas-projeto': Clock,
  'adiantamentos-extras': DollarSign,
  'documentos-usuario': FileText,
  notificacoes: AlertCircle,
  parcelas: DollarSign
};

// Source colors
const SOURCE_COLORS = {
  agendamentos: 'bg-purple-100 text-purple-700',
  projetos: 'bg-blue-100 text-blue-700',
  visitas: 'bg-green-100 text-green-700',
  demandas: 'bg-orange-100 text-orange-700',
  'pre-producao': 'bg-cyan-100 text-cyan-700',
  entregas: 'bg-yellow-100 text-yellow-700',
  'contatos-producao': 'bg-pink-100 text-pink-700',
  montagem: 'bg-indigo-100 text-indigo-700',
  embalagem: 'bg-teal-100 text-teal-700',
  orcamentos: 'bg-amber-100 text-amber-700',
  'horas-extras': 'bg-red-100 text-red-700',
  'sabados-trabalhados': 'bg-violet-100 text-violet-700',
  ausencias: 'bg-gray-100 text-gray-700',
  ferias: 'bg-sky-100 text-sky-700',
  holerites: 'bg-emerald-100 text-emerald-700',
  ponto: 'bg-slate-100 text-slate-700',
  treinamentos: 'bg-rose-100 text-rose-700',
  despesas: 'bg-lime-100 text-lime-700',
  adiantamentos: 'bg-amber-100 text-amber-700',
  'documentos-fiscais': 'bg-stone-100 text-stone-700',
  contratos: 'bg-neutral-100 text-neutral-700',
  'horas-projeto': 'bg-blue-100 text-blue-700',
  'adiantamentos-extras': 'bg-orange-100 text-orange-700',
  'documentos-usuario': 'bg-zinc-100 text-zinc-700',
  notificacoes: 'bg-red-100 text-red-700',
  parcelas: 'bg-red-100 text-red-700'
};

export default function DemandBoard({ userRole = 'GERENCIA' }) {
  const queryClient = useQueryClient();

  const [viewMode, setViewMode] = useState('day');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [expandedColumns, setExpandedColumns] = useState({});
  const [activeTask, setActiveTask] = useState(null);
  const [activeTab, setActiveTab] = useState('financeiro');
  const [annotations, setAnnotations] = useState([]);

  useEffect(() => {
    if (userRole !== 'GERENCIA' && userRole !== 'RECEPCAO_COMPRAS_AGENDA') {
      setActiveTab('tarefas');
    }
  }, [userRole]);
  const [newAnnotation, setNewAnnotation] = useState('');
  const [newAnnotationColor, setNewAnnotationColor] = useState('yellow');
  const [newAnnotationBold, setNewAnnotationBold] = useState(false);
  const [newAnnotationImportant, setNewAnnotationImportant] = useState(false);
  const [editingAnnotationId, setEditingAnnotationId] = useState(null);
  const [editingAnnotationText, setEditingAnnotationText] = useState('');
  const [filters, setFilters] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  // Estado para o modal de baixa financeira ao arrastar parcela para Resolvido
  const [financialSettlementModal, setFinancialSettlementModal] = useState(null);
  const [settlePaymentMethod, setSettlePaymentMethod] = useState('PIX');
  const [settlePaymentDate, setSettlePaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [settleRegisterCashbox, setSettleRegisterCashbox] = useState(true);
  const [settleNotes, setSettleNotes] = useState('');

  // Buscar lista de usuários (apenas para GERENCIA)
  const { data: usersData } = useQuery({
    queryKey: ['users-select'],
    queryFn: () => userService.getUsers({ limit: 500 }),
    enabled: userRole === 'GERENCIA' || userRole === 'ADMIN',
    onError: (error) => {
      console.error('Erro ao carregar usuários:', error);
    }
  });

  // Processar usuários
  const users = (() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    if (Array.isArray(usersData?.data)) return usersData.data;
    if (Array.isArray(usersData)) return usersData;
    return [];
  })();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const updateStatusMutation = useMutation({
    mutationFn: ({ taskId, newStatus, ...extra }) => dashboardService.updateTaskStatus(taskId, newStatus, extra),
    onMutate: async ({ taskId, newStatus }) => {
      await queryClient.cancelQueries({ queryKey: ['dashboard-tasks'] });
      const previousData = queryClient.getQueryData(['dashboard-tasks', filters, selectedUserId]);

      queryClient.setQueryData(['dashboard-tasks', filters, selectedUserId], (old) => {
        if (!old) return old;
        const updatedTasks = old.tasks.map(t =>
          t.id === taskId ? { ...t, status: newStatus } : t
        );
        const updatedGrouped = {};
        for (const col of Object.keys(old.grouped)) {
          updatedGrouped[col] = updatedTasks.filter(t => t.status === col);
        }
        return { ...old, tasks: updatedTasks, grouped: updatedGrouped };
      });

      return { previousData };
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['dashboard-tasks', filters, selectedUserId], context.previousData);
      }
      toast.error(error?.response?.data?.message || 'Erro ao atualizar status');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-tasks'] });
    }
  });

  // Query - busca tarefas unificadas (inclui user_id se for GERENCIA)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard-tasks', filters, selectedUserId],
    queryFn: () => fetchTasks({ ...filters, user_id: selectedUserId }),
    staleTime: 0, // NÃO usa cache
    cacheTime: 0
  });

  // Query - busca anotações do backend
  const { data: annotationsData, refetch: refetchAnnotations } = useQuery({
    queryKey: ['annotations'],
    queryFn: async () => {
      const res = await annotationsService.getAnnotations();
      return res.data || [];
    }
  });

  // Carregar anotações do backend para o estado local
  React.useEffect(() => {
    if (annotationsData && Array.isArray(annotationsData)) {
      const mapped = annotationsData.map(a => ({
        id: a.id,
        title: a.title,
        color: a.color || 'yellow',
        isBold: a.is_bold || false,
        isImportant: a.is_important || false,
        created_at: a.created_at
      }));
      setAnnotations(mapped);
    }
  }, [annotationsData]);

  // Mutation - criar anotação
  const createAnnotationMutation = useMutation({
    mutationFn: (data) => annotationsService.createAnnotation(data),
    onSuccess: () => {
      refetchAnnotations();
    },
    onError: () => {
      toast.error('Erro ao criar anotação');
    }
  });

  // Mutation - atualizar anotação
  const updateAnnotationMutation = useMutation({
    mutationFn: ({ id, ...data }) => annotationsService.updateAnnotation(id, data),
    onSuccess: () => {
      refetchAnnotations();
    },
    onError: () => {
      toast.error('Erro ao atualizar anotação');
    }
  });

  // Mutation - deletar anotação
  const deleteAnnotationMutation = useMutation({
    mutationFn: (id) => annotationsService.deleteAnnotation(id),
    onSuccess: () => {
      refetchAnnotations();
    },
    onError: () => {
      toast.error('Erro ao deletar anotação');
    }
  });

  // Get paginated tasks for a column (acumulando cards para não ocultar os anteriores)
  const getPaginatedTasks = (columnId) => {
    const tasks = getColumnTasks(columnId);
    const currentPage = expandedColumns[columnId] || 1;
    const visible = tasks.slice(0, currentPage * ITEMS_PER_PAGE);
    const hasMore = tasks.length > visible.length;
    const isRestored = currentPage === 1;
    return {
      visible,
      total: tasks.length,
      hasMore,
      isRestored,
      currentPage
    };
  };

  // Load next page of items
  const loadMore = (columnId) => {
    const tasks = getColumnTasks(columnId);
    const currentPage = expandedColumns[columnId] || 1;
    const nextPage = currentPage + 1;
    const maxPage = Math.ceil(tasks.length / ITEMS_PER_PAGE);
    if (nextPage <= maxPage) {
      setExpandedColumns(prev => ({
        ...prev,
        [columnId]: nextPage
      }));
    }
  };

  // Restore column to initial state (first page)
  const restoreColumn = (columnId) => {
    setExpandedColumns(prev => ({
      ...prev,
      [columnId]: 1
    }));
  };

  // Get columns based on active tab
  const getColumns = () => {
    return activeTab === 'financeiro' ? FINANCEIRO_COLUMNS : TAREFAS_COLUMNS;
  };

  // Get tasks for a column (handles annotations specially)
  const getColumnTasks = (columnId) => {
    if (columnId === 'annotations') {
      return annotations;
    }
    return groupedByTab[columnId] || [];
  };

  // Add new annotation
  const addAnnotation = () => {
    if (!newAnnotation.trim()) return;
    createAnnotationMutation.mutate({
      title: newAnnotation.trim(),
      color: newAnnotationColor,
      is_bold: newAnnotationBold,
      is_important: newAnnotationImportant
    });
    setNewAnnotation('');
    setNewAnnotationBold(false);
    setNewAnnotationImportant(false);
  };

  // Update annotation
  const updateAnnotation = (id, updates) => {
    updateAnnotationMutation.mutate({ id, ...updates });
  };

  // Start editing annotation
  const startEditingAnnotation = (annotation) => {
    setEditingAnnotationId(annotation.id);
    setEditingAnnotationText(annotation.title);
  };

  // Save edited annotation
  const saveEditedAnnotation = () => {
    if (!editingAnnotationText.trim()) return;
    updateAnnotationMutation.mutate({
      id: editingAnnotationId,
      title: editingAnnotationText.trim()
    });
    setEditingAnnotationId(null);
    setEditingAnnotationText('');
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingAnnotationId(null);
    setEditingAnnotationText('');
  };

  // Delete annotation
  const deleteAnnotation = (id) => {
    deleteAnnotationMutation.mutate(id);
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const task = filteredTasks.find(t => t.id === active.id);
    setActiveTask(task);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const activeTask = filteredTasks.find(t => t.id === activeId);
    if (!activeTask) return;

    let newStatus;
    if (overId.startsWith('column-')) {
      newStatus = overId.replace('column-', '');
    } else {
      const overTask = filteredTasks.find(t => t.id === overId);
      if (overTask) newStatus = overTask.status;
    }

    if (newStatus && newStatus !== activeTask.status) {
      // Trava de baixa financeira: abre modal se for parcela sendo movida para resolvido
      if (activeTask.source === 'parcelas' && (newStatus === 'resolvido' || newStatus === 'done')) {
        setFinancialSettlementModal({
          task: activeTask,
          targetStatus: newStatus
        });
        setSettlePaymentMethod('PIX');
        setSettlePaymentDate(new Date().toISOString().split('T')[0]);
        setSettleRegisterCashbox(true);
        setSettleNotes('');
        return;
      }

      updateStatusMutation.mutate({ taskId: activeId, newStatus });
    }
  };

  // Navegação de datas
  const navigateDate = (direction) => {
    const current = new Date(filters.start_date);
    const days = viewMode === 'day' ? 1 : viewMode === 'week' ? 7 : 30;
    current.setDate(current.getDate() + (direction * days));

    const newStart = new Date(current);
    const newEnd = new Date(current);
    newEnd.setDate(newEnd.getDate() + days - 1);

    setFilters({
      start_date: newStart.toISOString().split('T')[0],
      end_date: newEnd.toISOString().split('T')[0]
    });
  };

  // Reset para hoje
  const goToToday = () => {
    const today = new Date();
    setFilters({
      start_date: today.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0]
    });
  };

  // Renderizar data range
  const getDateRangeLabel = () => {
    const start = new Date(filters.start_date);
    const end = new Date(filters.end_date);

    if (viewMode === 'day') {
      return start.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
    } else if (viewMode === 'week') {
      return `${start.toLocaleDateString('pt-BR', { day: 'numeric' })} - ${end.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })}`;
    } else {
      return `${start.toLocaleDateString('pt-BR', { month: 'long' })}`;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando tarefas de todo o sistema...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-2">Erro ao carregar tarefas</p>
          <button
            onClick={() => queryClient.invalidateQueries(['dashboard-tasks'])}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const allTasks = data?.tasks || [];

  const filteredTasks = allTasks.filter(task => {
    if (activeTab === 'financeiro') {
      return task.source === 'parcelas';
    } else {
      return task.source !== 'parcelas' && task.source !== 'despesas';
    }
  });

  const groupedByTab = {
    overdue: filteredTasks.filter(t => t.status === 'overdue'),
    due_soon: filteredTasks.filter(t => t.status === 'due_soon'),
    em_tratativa: filteredTasks.filter(t => t.status === 'em_tratativa'),
    resolvido: filteredTasks.filter(t => t.status === 'resolvido'),
    pending: filteredTasks.filter(t => t.status === 'pending'),
    in_progress: filteredTasks.filter(t => t.status === 'in_progress'),
    review: filteredTasks.filter(t => t.status === 'review'),
    done: filteredTasks.filter(t => t.status === 'done'),
    blocked: filteredTasks.filter(t => t.status === 'blocked')
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-3 py-3 sm:px-4 sm:py-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-gray-800">Minhas Tarefas</h1>
            <p className="text-xs sm:text-sm text-gray-500">
              {filteredTasks.length} {activeTab === 'financeiro' ? 'parcelas' : 'tarefas'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-1 sm:gap-2 bg-gray-100 rounded-lg p-1">
              <button onClick={() => { navigateDate(-1); refetch(); }} className="p-1.5 sm:p-1 hover:bg-white rounded touch-manipulation">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => { goToToday(); refetch(); }} className="px-2 py-1 text-xs sm:text-sm hover:bg-white rounded touch-manipulation">
                Hoje
              </button>
              <button onClick={() => navigateDate(1)} className="p-1.5 sm:p-1 hover:bg-white rounded touch-manipulation">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => {
                  setFilters(f => ({ ...f, start_date: new Date().toISOString().split('T')[0], end_date: new Date().toISOString().split('T')[0] }));
                  setViewMode('day');
                }}
                className={`px-2 py-1 text-xs sm:text-sm rounded touch-manipulation ${viewMode === 'day' ? 'bg-white shadow' : 'hover:bg-white'}`}
              >
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                <span className="hidden xs:inline">Dia</span>
              </button>
              <button
                onClick={() => { navigateDate(0); setViewMode('week'); }}
                className={`px-2 py-1 text-xs sm:text-sm rounded touch-manipulation ${viewMode === 'week' ? 'bg-white shadow' : 'hover:bg-white'}`}
              >
                <Layout className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                <span className="hidden xs:inline">Semana</span>
              </button>
              <button
                onClick={() => { setViewMode('month'); navigateDate(0); }}
                className={`px-2 py-1 text-xs sm:text-sm rounded touch-manipulation ${viewMode === 'month' ? 'bg-white shadow' : 'hover:bg-white'}`}
              >
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                <span className="hidden sm:inline">Mês</span>
              </button>
            </div>

            <span className="text-xs sm:text-sm font-medium text-gray-600 px-1">{getDateRangeLabel()}</span>

            {(userRole === 'GERENCIA' || userRole === 'ADMIN') && (
              <select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
                className="px-2 py-1.5 border rounded-lg text-xs sm:text-sm bg-white touch-manipulation max-w-[140px] sm:max-w-none"
              >
                <option value="">Colaboradores</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-3 py-2 flex gap-2 overflow-x-auto scrollbar-hide">
        <span className="text-xs sm:text-sm text-gray-500 self-center whitespace-nowrap">Exibir:</span>
        {(userRole === 'GERENCIA' || userRole === 'RECEPCAO_COMPRAS_AGENDA') && (
          <button
            onClick={() => setActiveTab('financeiro')}
            className={`px-3 py-1 text-xs sm:text-sm rounded-lg transition-colors whitespace-nowrap touch-manipulation ${activeTab === 'financeiro' ? 'bg-red-100 text-red-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Financeiro
          </button>
        )}
        <button
          onClick={() => setActiveTab('tarefas')}
          className={`px-3 py-1 text-xs sm:text-sm rounded-lg transition-colors whitespace-nowrap touch-manipulation ${activeTab === 'tarefas' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          Tarefas
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 h-full">
            {getColumns().map(column => {
              const Icon = column.icon;
              const { visible: columnTasks, total, hasMore, isRestored, currentPage } = getPaginatedTasks(column.id);

              const columnWidth = column.width || COLUMN_WIDTH;
              return (
                <DroppableColumnWrapper key={column.id} id={`column-${column.id}`}>
                <div
                  className={`w-full flex flex-col rounded-lg border-2 ${column.color} max-h-[calc(100vh-220px)] sm:max-h-[calc(100vh-180px)]`}
                >
                  <div className="px-2 sm:px-1.5 py-1.5 sm:py-1 border-b border-gray-200 flex items-center justify-between bg-white/50 sticky top-0 z-10">
                    <div className="flex items-center gap-1.5">
                      <Icon className="w-3.5 h-3.5 sm:w-3 sm:h-3 text-gray-600" />
                      <span className="font-semibold text-gray-700 text-xs sm:text-xs">{column.title}</span>
                    </div>
                    <span className="text-xs bg-white px-1.5 py-0.5 rounded-full font-medium">
                      {total}
                    </span>
                  </div>

                  {column.isAnnotations ? (
                    <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px]">
                      {columnTasks.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-8">Nenhuma anotação</p>
                      ) : (
                        columnTasks.map(annotation => {
                          const colorInfo = COLORS.find(c => c.name === annotation.color) || COLORS[0];
                          const isEditing = editingAnnotationId === annotation.id;
                          return (
                            <div
                              key={annotation.id}
                              className={`p-2.5 rounded-lg shadow-sm border-l-4 ${colorInfo.border} ${colorInfo.bg} group hover:shadow-md transition-shadow`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2 flex-1">
                                  {annotation.isImportant && (
                                    <Pin className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5 fill-red-500" />
                                  )}
                                  {isEditing ? (
                                    <div className="flex-1 flex flex-col gap-1">
                                      <textarea
                                        value={editingAnnotationText}
                                        onChange={(e) => setEditingAnnotationText(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEditedAnnotation(); }
                                          if (e.key === 'Escape') cancelEditing();
                                        }}
                                        rows={2}
                                        className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                                        autoFocus
                                      />
                                      <div className="flex gap-1">
                                        <button
                                          onClick={saveEditedAnnotation}
                                          className="px-2 py-0.5 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                        >
                                          Salvar
                                        </button>
                                        <button
                                          onClick={cancelEditing}
                                          className="px-2 py-0.5 bg-gray-400 text-white rounded text-xs hover:bg-gray-500"
                                        >
                                          Cancelar
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className={`text-xs text-gray-800 flex-1 whitespace-pre-wrap break-words ${annotation.isBold ? 'font-bold' : ''}`}>
                                      {annotation.title}
                                    </p>
                                  )}
                                </div>
                                {!isEditing && (
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => startEditingAnnotation(annotation)}
                                      className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => deleteAnnotation(annotation.id)}
                                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                      {hasMore && (
                        <button
                          onClick={() => loadMore(column.id)}
                          className="w-full py-1.5 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors"
                        >
                          Carregar mais ({Math.min(currentPage * ITEMS_PER_PAGE, total)} de {total})
                        </button>
                      )}
                      {!isRestored && (
                        <button
                          onClick={() => restoreColumn(column.id)}
                          className="w-full py-1.5 text-xs text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                        >
                          Restaurar
                        </button>
                      )}
                      <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                        <textarea
                          ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                          value={newAnnotation}
                          onChange={(e) => {
                            setNewAnnotation(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addAnnotation(); } }}
                          placeholder="Nova anotação..."
                          rows={1}
                          className="w-full min-h-[44px] max-h-32 px-3 py-2 text-sm sm:text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none overflow-hidden touch-manipulation"
                        />
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 hidden xs:inline">Cor:</span>
                            {COLORS.map(color => (
                              <button
                                key={color.name}
                                onClick={() => setNewAnnotationColor(color.name)}
                                className={`w-6 h-6 sm:w-5 sm:h-5 rounded-full ${color.bg} border-2 ${newAnnotationColor === color.name ? 'border-gray-600 scale-110' : 'border-transparent'} hover:scale-110 transition-transform touch-manipulation`}
                                title={color.name}
                              />
                            ))}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setNewAnnotationBold(!newAnnotationBold)}
                              className={`p-1.5 sm:p-1 rounded touch-manipulation ${newAnnotationBold ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                              title="Negrito"
                            >
                              <span className="text-xs font-bold">B</span>
                            </button>
                            <button
                              onClick={() => setNewAnnotationImportant(!newAnnotationImportant)}
                              className={`p-1.5 rounded ${newAnnotationImportant ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                              title="Importante"
                            >
                              <Pin className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={addAnnotation}
                          className="w-full px-3 py-2.5 sm:py-2 bg-purple-500 text-white rounded-lg text-sm sm:text-xs hover:bg-purple-600 flex items-center justify-center gap-2 touch-manipulation"
                        >
                          <Plus className="w-4 h-4" />
                          Adicionar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <SortableContext items={columnTasks.map(t => t.id)} strategy={rectSortingStrategy}>
                      <div className="flex-1 overflow-y-auto p-1.5 space-y-1.5 min-h-[150px]">
                        {columnTasks.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-4">Nenhuma tarefa</p>
                        ) : (
                          <>
                            {columnTasks.map(task => (
                              <SortableTaskCard key={task.id} task={task} />
                            ))}
                            {hasMore && (
                              <button
                                onClick={() => loadMore(column.id)}
                                className="w-full py-2 sm:py-1.5 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors touch-manipulation"
                              >
                                Carregar mais ({Math.min(currentPage * ITEMS_PER_PAGE, total)} de {total})
                              </button>
                            )}
                            {!isRestored && (
                              <button
                                onClick={() => restoreColumn(column.id)}
                                className="w-full py-2 sm:py-1.5 text-xs text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors touch-manipulation"
                              >
                                Restaurar
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </SortableContext>
                  )}
                </div>
                </DroppableColumnWrapper>
              );
            })}
          </div>
          <DragOverlay>
            {activeTask ? <TaskCard task={activeTask} isDragging /> : null}
          </DragOverlay>
        </div>
      </DndContext>

      {/* Modal de Trava de Baixa Financeira ao Arrastar Parcela para Resolvido */}
      {financialSettlementModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-100">
            {/* Cabeçalho */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-emerald-50/60">
              <div className="flex items-center gap-2.5">
                <div className="p-2.5 bg-emerald-100 text-emerald-800 rounded-xl">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Confirmar Baixa e Entrada no Caixa
                  </h3>
                  <p className="text-xs text-gray-500">
                    Lançamento financeiro real da parcela no Livro Caixa
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFinancialSettlementModal(null)}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Formulário */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateStatusMutation.mutate({
                  taskId: financialSettlementModal.task.id,
                  newStatus: financialSettlementModal.targetStatus,
                  payment_method: settlePaymentMethod,
                  payment_date: settlePaymentDate,
                  register_cashbox: settleRegisterCashbox,
                  notes: settleNotes
                });
                setFinancialSettlementModal(null);
              }}
              className="p-5 space-y-4"
            >
              {/* Card Resumo da Parcela */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Parcela a Liquidar</span>
                    <h4 className="text-sm font-bold text-gray-900 line-clamp-2">
                      {financialSettlementModal.task.title}
                    </h4>
                    {financialSettlementModal.task.description && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {financialSettlementModal.task.description}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Valor</span>
                    <p className="text-base font-extrabold text-emerald-700 font-mono">
                      {financialSettlementModal.task.amount
                        ? formatCurrency(financialSettlementModal.task.amount)
                        : (financialSettlementModal.task.title.match(/R\$\s*([\d.,]+)/) ? financialSettlementModal.task.title.match(/R\$\s*([\d.,]+)/)[0] : '')}
                    </p>
                  </div>
                </div>
                {financialSettlementModal.task.due_date && (
                  <div className="text-[11px] text-gray-500 flex items-center gap-1 pt-1 border-t border-gray-200">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span>Vencimento original: {new Date(financialSettlementModal.task.due_date).toLocaleDateString('pt-BR')}</span>
                  </div>
                )}
              </div>

              {/* Data e Forma de Pagamento */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Data do Pagamento *
                  </label>
                  <input
                    type="date"
                    required
                    value={settlePaymentDate}
                    onChange={(e) => setSettlePaymentDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs font-medium focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Forma de Pagamento *
                  </label>
                  <select
                    value={settlePaymentMethod}
                    onChange={(e) => setSettlePaymentMethod(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="PIX">PIX</option>
                    <option value="DINHEIRO">Dinheiro em Espécie</option>
                    <option value="CARTAO">Cartão de Crédito / Débito</option>
                    <option value="TRANSFERENCIA">Transferência Bancária</option>
                    <option value="CHEQUE">Cheque</option>
                    <option value="BALANCO_CAIXA">Balanço de Caixa</option>
                    <option value="OUTRO">Outro / Acordo Direto</option>
                  </select>
                </div>
              </div>

              {/* Checkbox Caixa da Empresa */}
              <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl space-y-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settleRegisterCashbox}
                    onChange={(e) => setSettleRegisterCashbox(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-blue-950 flex items-center gap-1.5">
                    <ArrowDownCircle className="w-4 h-4 text-blue-600" />
                    <span>Lançar Entrada no Caixa da Empresa</span>
                  </span>
                </label>
                <p className="text-[11px] text-blue-800 pl-6 leading-relaxed">
                  Gera uma movimentação de <strong>ENTRADA (INCOME)</strong> no Livro Caixa, aumentando o saldo financeiro da empresa na data informada.
                </p>
              </div>

              {/* Observações */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Observações / Comprovante (Opcional)
                </label>
                <input
                  type="text"
                  value={settleNotes}
                  onChange={(e) => setSettleNotes(e.target.value)}
                  placeholder="Ex: Recebido em mãos pelo cliente, comprovante anexado..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Botões */}
              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setFinancialSettlementModal(null)}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm transition-all flex items-center gap-1.5"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Confirmar Recebimento e Liquidar</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function TaskCard({ task, isDragging }) {
  const Icon = SOURCE_ICONS[task.source] || FileText;
  const colorClass = SOURCE_COLORS[task.source] || 'bg-gray-100 text-gray-700';

  return (
    <div className={`bg-white p-2 sm:p-1.5 rounded shadow-sm border border-gray-200 hover:shadow transition-shadow touch-manipulation ${isDragging ? 'opacity-50 shadow-lg rotate-2' : ''}`}>
      {/* Drag handle */}
      <div className="flex items-center justify-between mb-1">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${colorClass}`}>
          <Icon className="w-2.5 h-2.5" />
          <span className="hidden xs:inline">{task.source_label || task.source}</span>
        </span>
        <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
      </div>

      {/* Title */}
      <h3 className="font-medium text-gray-800 text-xs sm:text-xs mb-1 line-clamp-2">{task.title}</h3>

      {/* Description */}
      {task.description && (
        <p className="text-xs text-gray-500 line-clamp-1 hidden sm:block">{task.description}</p>
      )}

      {/* Due date */}
      {task.due_date && (
        <div className="mt-1">
          <div className="px-1.5 py-0.5 bg-blue-50 rounded text-xs font-medium text-blue-700 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(task.due_date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
          </div>
        </div>
      )}
    </div>
  );
}

function SortableTaskCard({ task }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 'auto'
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaskCard task={task} isDragging={isDragging} />
    </div>
  );
}

function DroppableColumnWrapper({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={isOver ? 'bg-blue-50' : ''}>
      {children}
    </div>
  );
}
